// processPostDraftFiles.js
'use strict';
import { v4 as uuidv4 } from 'uuid';

import { MERGE_OPERATION_DATA, REMOVE_OPERATION_DATA } from '../ActionTypes.js';
import {
  ASSET_FILE_SIZE_MAX,
  MESSAGE_VIDEO_LENGTH_SEC_MIN_MAX,
  POST_ASSET_MAX_QUANTITIES,
  POST_VIDEO_LENGTH_MIN,
  POST_VIDEO_LENGTH_MAX,
} from '../RemoteConfigKeys.js';

import removeAssets from '../action/removeAssets.js';
import addAsset from '../action/addAsset.js';
import pushToastr from '../action/pushToastr.js';

import getOperationData from '../selector/getOperationData.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';

import { Purpose as AssetPurpose } from '../resource/assetConstants.js';
import { UploadJobType, PostDraftField } from '../resource/uploadConstants.js';
import formatBytes from '../resource/formatBytes.js';
import getVideoDuration from '../resource/getVideoDuration.js';
import { upload as uploadDebug } from '../resource/debug.js';

const uploadLog = uploadDebug.extend('log:action/processPostDraftFiles.js');
const uploadError = uploadDebug.extend('error:action/processPostDraftFiles.js');

/**
 * Proceed upload input file changed
 * @kind action
 * @param {AssetPurpose} {purpose} - assets purpose.
 * @param {array|undefined} {files} - files.
 * @return {Promise} Action promise.
 */
const processPostDraftFiles =
  ({ purpose, files } = {}) =>
  async (dispatch, getState) => {
    uploadLog('execute processPostDraftFiles');
    try {
      const currentAssetIds = getOperationData(
        getState(),
        ['postDraft', purpose],
        'assetIds'
      );
      if (currentAssetIds) {
        dispatch(
          removeAssets({
            assetIds: currentAssetIds,
          })
        );
      }
      if (!files) {
        return dispatch({
          type: REMOVE_OPERATION_DATA,
          payload: {
            selectPath: ['postDraft', purpose],
          },
        });
      }
      uploadLog('pre check assets', {
        postDraft: getOperationData(getState(), ['postDraft', purpose]),
      });
      await dispatch(checkAssets({ purpose, files }));
      uploadLog('post check assets', {
        postDraft: getOperationData(getState(), ['postDraft', purpose]),
      });
      const isValid = getOperationData(
        getState(),
        ['postDraft', purpose],
        'isValid'
      );
      if (!isValid) {
        return dispatch(
          pushToastr({
            textKey: getOperationData(
              getState(),
              ['postDraft', purpose, 'error'],
              'i18nKey'
            ),
            textKeyOption: getOperationData(
              getState(),
              ['postDraft', purpose, 'error'],
              'i18nOption'
            ),
            color: 'error',
          })
        );
      }
      const uploadJobId = getOperationData(
        getState(),
        ['postDraft', PostDraftField.UPLOAD_JOB_ID],
        'value'
      );
      if (!uploadJobId) {
        uploadLog(
          'early return at processPostDraftFiles if upload job id is empty'
        );
        return dispatch({ type: '' });
      }
      const assetIds = await Promise.all(
        files.map(async file => {
          const assetId = uuidv4();
          const isImage = file.type.startsWith('image/');
          dispatch(
            addAsset({
              assetId,
              uploadJobId,
              purpose,
              file,
              duration: isImage
                ? 0
                : Math.floor(await getVideoDuration({ file })),
            })
          );
          return assetId;
        })
      );
      dispatch({
        type: MERGE_OPERATION_DATA,
        payload: {
          selectPath: ['postDraft', purpose],
          data: {
            assetIds,
            error: {
              i18nKey: undefined,
              i18nOption: undefined,
            },
          },
        },
      });
    } catch (error) {
      uploadError('error at processPostDraftFiles', { error });
      dispatch(
        pushToastr({
          textKey: 'alert_incorrect_format',
          color: 'error',
        })
      );
      return dispatch({
        type: MERGE_OPERATION_DATA,
        payload: {
          selectPath: ['postDraft', purpose],
          data: {
            error: {
              i18nKey: error.message,
              i18nOption: undefined,
            },
          },
        },
      });
    }
  };

export default processPostDraftFiles;

/**
 * Check assets
 * @kind action
 * @param {AssetPurpose} {purpose} - assets purpose.
 * @param {array} {files} - files.
 * @return {Promise} Action promise.
 */
const checkAssets =
  ({ purpose, files = [] }) =>
  async (dispatch, getState) => {
    uploadLog('execute check assets');
    const uploadJobType = getOperationData(
      getState(),
      ['postDraft', PostDraftField.ENTRY],
      'value'
    );
    if (AssetPurpose.POST === purpose) {
      const fileQuantityResult = checkFileQuantities({
        files,
        maxFileQuantities: getRemoteConfigData(
          getState(),
          POST_ASSET_MAX_QUANTITIES
        ),
      });
      if (!fileQuantityResult.isValid) {
        return dispatch({
          type: MERGE_OPERATION_DATA,
          payload: {
            selectPath: ['postDraft', purpose],
            data: fileQuantityResult,
          },
        });
      }
    }
    return Promise.all(
      files.map(async file => {
        const isVideo = file.type.startsWith('video/');
        const isImage = file.type.startsWith('image/');
        const tasks = [];
        switch (purpose) {
          case AssetPurpose.POST: {
            switch (uploadJobType) {
              case UploadJobType.POST: {
                if (isImage) {
                  tasks.push(
                    checkFileSize({
                      file,
                      maxFileSize: getRemoteConfigData(
                        getState(),
                        ASSET_FILE_SIZE_MAX
                      ),
                    })
                  );
                } else if (isVideo) {
                  tasks.push(
                    checkFileSize({
                      file,
                      maxFileSize: getRemoteConfigData(
                        getState(),
                        ASSET_FILE_SIZE_MAX
                      ),
                    })
                  );
                  const postDurationMin = getRemoteConfigData(
                    getState(),
                    POST_VIDEO_LENGTH_MIN
                  );
                  const postDurationMax = getRemoteConfigData(
                    getState(),
                    POST_VIDEO_LENGTH_MAX
                  );
                  tasks.push(
                    checkVideoDuration({
                      file,
                      maxDurationSeconds: postDurationMax,
                      minDurationSeconds: postDurationMin,
                      isDurationNullable: false,
                      videoDurationI18nKey: 'alert_can_not_upload_video',
                      videoDurationI18nOption: [
                        postDurationMin / 60,
                        postDurationMax / 60,
                      ],
                    })
                  );
                } else {
                  tasks.push({
                    isValid: false,
                    error: {
                      i18nKey: 'alert_incorrect_format',
                    },
                  });
                }
                break;
              }
              case UploadJobType.STORY: {
                if (isImage) {
                  tasks.push(
                    checkFileSize({
                      file,
                      maxFileSize: getRemoteConfigData(
                        getState(),
                        ASSET_FILE_SIZE_MAX
                      ),
                    })
                  );
                } else if (isVideo) {
                  const [min, max] = getRemoteConfigData(
                    getState(),
                    MESSAGE_VIDEO_LENGTH_SEC_MIN_MAX
                  ) || [0, 0];
                  tasks.push(
                    checkFileSize({
                      file,
                      maxFileSize: getRemoteConfigData(
                        getState(),
                        ASSET_FILE_SIZE_MAX
                      ),
                    })
                  );
                  tasks.push(
                    checkVideoDuration({
                      file,
                      maxDurationSeconds: +max,
                      minDurationSeconds: +min,
                      isDurationNullable: false,
                      videoDurationI18nKey: 'video_over_30s',
                    })
                  );
                } else {
                  tasks.push({
                    isValid: false,
                    error: {
                      i18nKey: 'alert_incorrect_format',
                    },
                  });
                }
                break;
              }
            }
            break;
          }
          case AssetPurpose.AUP: {
            tasks.push(
              checkFileSize({
                file,
                maxFileSize: +getRemoteConfigData(
                  getState(),
                  ASSET_FILE_SIZE_MAX
                ),
              })
            );
            break;
          }
        }
        const results = await Promise.all(tasks);
        const isValid =
          results.length > 0 && results.every(({ isValid }) => isValid);
        if (isValid) {
          return dispatch({
            type: MERGE_OPERATION_DATA,
            payload: {
              selectPath: ['postDraft', purpose],
              data: {
                isValid: true,
              },
            },
          });
        }
        return dispatch({
          type: MERGE_OPERATION_DATA,
          payload: {
            selectPath: ['postDraft', purpose],
            data: results.find(({ isValid }) => !isValid),
          },
        });
      })
    );
  };

/**
 * Check file size.
 * @param {File} {file} - file.
 * @param {number} {maxFileSize} - accepted max file size.
 * @return {object} return check result object.
 */
const checkFileSize = async ({ file, maxFileSize }) => {
  const isValidFileSize = maxFileSize ? file.size <= maxFileSize : true;
  if (!isValidFileSize) {
    return {
      isValid: false,
      error: {
        i18nKey: 'upload_file_error_hint',
        i18nOption: {
          size: formatBytes({
            bytes: maxFileSize,
          }),
        },
      },
    };
  }
  return {
    isValid: true,
    error: {
      i18nKey: undefined,
      i18nOption: undefined,
    },
  };
};

/**
 * Check file quantities.
 * @param {array} {files} - files.
 * @param {number} {maxFileQuantities} - accepted max file quantities.
 * @return {object} return check result object.
 */
const checkFileQuantities = ({ files = [], maxFileQuantities }) => {
  const isValidQuantities = files.length <= maxFileQuantities;
  if (!isValidQuantities) {
    return {
      isValid: false,
      error: {
        i18nKey: 'post_asset_quantities_error',
        i18nOption: {
          quantities: maxFileQuantities,
        },
      },
    };
  }
  return {
    isValid: true,
    error: {
      i18nKey: undefined,
      i18nOption: undefined,
    },
  };
};

/**
 * Check video duration.
 * @param {File} {file} - file.
 * @param {number|undefined} {maxDurationSeconds} - accepted max video duration seconds.
 * @param {number|undefined} {minDurationSeconds} - accepted min video duration seconds.
 * @param {boolean} {isDurationNullable} - is duration nullable.
 * @param {string|undefined} {videoDurationI18nKey} - invalid i18n key.
 * @param {array|object|undefined} {videoDurationI18nOption} - invalid i18n option.
 * @return {object} return check result object.
 */
const checkVideoDuration = async ({
  file,
  maxDurationSeconds,
  minDurationSeconds,
  isDurationNullable,
  videoDurationI18nKey,
  videoDurationI18nOption,
}) => {
  const duration = await getVideoDuration({ file });
  const durationSeconds = duration ? Math.floor(duration) : duration;
  const isValidDuration =
    isDurationNullable || (!isDurationNullable && duration);
  const isValidMin = minDurationSeconds
    ? durationSeconds >= minDurationSeconds
    : true;
  const isValidMax = maxDurationSeconds
    ? durationSeconds <= maxDurationSeconds
    : true;
  return isValidDuration && isValidMin && isValidMax
    ? {
        isValid: true,
        error: {
          i18nKey: undefined,
          i18nOption: undefined,
        },
      }
    : {
        isValid: false,
        error: {
          i18nKey: videoDurationI18nKey,
          i18nOption: videoDurationI18nOption,
        },
      };
};
